import React, { useEffect } from 'react';
import { FormHelperText, Grid } from '@mui/material';
import { useField, useFormikContext } from 'formik';
import { AdminFieldLabel } from '@/admin/shared/AdminFieldLabel';
import { AdminInsightIdPicker } from '@/admin/shared/pickers/AdminInsightIdPicker';
import { useListAllBrokerGroupsQuery } from '@/admin/brokerGroups/api/brokerGroupsApi';
import {
	USER_INSIGHT_FIELD_TYPES,
	toInsightList,
	useListInsightFieldsQuery,
} from '@/admin/api/insightFieldsApi';
import { INSIGHT_REQUIRED_MESSAGES, isInsightIdRequired } from '../../userValidationSchema';
import { USER_FIELD_LABELS } from '../userFormValues';

/**
 * One of the user drawers' Insight id fields (`name`: salesTeamId, serviceTeamId,
 * accountTypeId or authorisedRepId), as a dropdown of that Insight list for the primary
 * broker group's ledger (AdminInsightIdPicker). The form keeps the id, or '' for none; the
 * admin API checks it's in Insight when it's saved.
 *
 * needsInsightIds (sales team and authorised rep of a new user who needs them) makes it
 * required, marked on the label and checked by Formik with this field, unless its list has
 * loaded with nothing to pick, or the ledger's Insight doesn't offer it (isInsightIdRequired).
 */
export const InsightIdField = ({ name, needsInsightIds = false }) => {
	const { values, touched, errors, setFieldValue, validateField } = useFormikContext();
	const { data: brokerGroups = [] } = useListAllBrokerGroupsQuery();
	const ledger = brokerGroups.find(({ id }) => id === values.brokerGroupId)?.ledger;
	const fieldType = USER_INSIGHT_FIELD_TYPES[name];
	// The same cached list the picker shows
	// currentData, not data, so another ledger's list doesn't decide this one's rule
	const { currentData: data, isError, error: listError } = useListInsightFieldsQuery(
		{ ledger, fieldType },
		{ skip: !ledger },
	);
	// Once it has loaded or failed; required while it loads (isSuccess would still be true
	// from the previous ledger's list)
	const required =
		needsInsightIds &&
		isInsightIdRequired((data || isError) && toInsightList({ data, isError, error: listError }));
	useField({
		name,
		validate: (value) => (required && value === '' && INSIGHT_REQUIRED_MESSAGES[name]) || undefined,
	});
	// The list loads after the primary broker group changes, so check again once it's known
	// whether the field is required (useField's validate is updated first)
	useEffect(() => {
		validateField(name);
	}, [required, name, validateField]);
	const error = touched[name] && errors[name];

	return (
		<Grid size={{ xs: 12, sm: 6 }}>
			<AdminFieldLabel name={name} required={required}>
				{USER_FIELD_LABELS[name]}
			</AdminFieldLabel>
			<AdminInsightIdPicker
				ledger={ledger}
				fieldType={fieldType}
				// '' (no id) becomes null; ids are positive, so no real id is lost
				value={Number(values[name]) || null}
				onChange={(id) => setFieldValue(name, id ?? '')}
				error={Boolean(error)}
			/>
			{error && <FormHelperText error>{error}</FormHelperText>}
		</Grid>
	);
};

InsightIdField.displayName = 'InsightIdField';
